<script>
    import * as Sentry from '@sentry/vue';
    import { deviceType } from 'detect-it';
    import { isFunction } from 'lodash-es';
    import TooltipException from '@/exceptions/TooltipException.js';

    export default {
        name: 'ActivixTooltip',

        props: {
            content: {
                type: [String, Function],
                default: '',
            },
            placement: {
                type: String,
                default: 'top',
            },
            onlyOnTruncate: {
                type: Boolean,
                default: false,
            },
            onlyOnVerticalTruncate: {
                type: Boolean,
                default: false,
            },
            showOnMobile: {
                type: Boolean,
                default: false,
            },
        },

        data() {
            return {
                instance: null,
            };
        },

        computed: {
            // Wrapper for global deviceType to make it easier to mock in tests
            deviceType() {
                return deviceType;
            },
        },

        methods: {
            async onMouseEnter() {
                if (this.instance) {
                    this.instance.setContent(await this.getContent());
                } else {
                    await this.init(true);
                }

                this.$nextTick(async () => {
                    const content = await this.getContent();
                    const hasNonEmptyContent = content && content.trim() !== '';
                    if (hasNonEmptyContent && this.instance) {
                        this.instance.show();
                    }
                });
            },

            onMouseLeave() {
                if (this.instance) {
                    this.instance.hide();
                }
            },

            async getContent() {
                if (isFunction(this.content)) {
                    return this.content();
                }

                return this.content ? this.content.trim() : '';
            },

            isOnlyOnTruncate() {
                return this.onlyOnTruncate && !(this.$el.offsetWidth < this.$el.scrollWidth);
            },

            isOnlyOnVerticalTruncate() {
                return this.onlyOnVerticalTruncate && !(this.$el.offsetHeight < this.$el.scrollHeight);
            },

            async init(forceRender = false) {
                const isMobile = this.lgLayout || this.deviceType === 'touchOnly';
                if (!this.showOnMobile && isMobile) {
                    return;
                }

                if (!forceRender) {
                    return;
                }

                if (this.isOnlyOnTruncate() && this.isOnlyOnVerticalTruncate()) {
                    return;
                }

                const content = await this.getContent();

                if (!content) {
                    return;
                }

                await this.destroy();

                try {
                    this.instance = this.$tooltip.init(this.$el, {
                        content,
                        placement: this.placement,
                        trigger: 'manual',
                    });
                } catch (e) {
                    if (!(e instanceof TooltipException)) {
                        throw e;
                    }

                    Sentry.withScope((scope) => {
                        scope.setTag('tooltip.content', e.tooltipOptions.content);
                        scope.setExtra('options', e.tooltipOptions);
                        scope.setExtra('possibleCause', e.possibleCause);
                        Sentry.captureException(e);
                    });
                }
            },

            async destroy() {
                if (!this.instance) {
                    return;
                }

                if (typeof this.instance.destroy === 'function') {
                    this.instance.destroy(true);
                    this.instance = null;
                }

                await this.$nextTick();
            },
        },

        async mounted() {
            await this.$nextTick();

            this.$el.addEventListener('mouseenter', this.onMouseEnter);
            this.$el.addEventListener('mouseleave', this.onMouseLeave);
        },

        beforeDestroy() {
            this.destroy();

            this.$el.removeEventListener('mouseenter', this.onMouseEnter);
            this.$el.removeEventListener('mouseleave', this.onMouseLeave);
        },

        render() {
            return this.$slots.default;
        },
    };
</script>
